// src/lib/analytics.ts
// PostHog is ~80 KB gzipped. Keep it off the critical path: pages import this tiny
// facade, calls are queued, and the real SDK is loaded once the browser is idle.
import { browser } from '$app/environment';
import type { PostHog, PostHogConfig } from 'posthog-js';

type Call = (ph: PostHog) => void;

let instance: PostHog | null = null;
let loading: Promise<PostHog | null> | null = null;
const queue: Call[] = [];

function run(fn: Call) {
	if (!browser) return;
	if (instance) fn(instance);
	else queue.push(fn);
}

/** Load and initialise PostHog after first paint. Safe to call more than once. */
export function initAnalytics(token: string | undefined, config: Partial<PostHogConfig>) {
	if (!browser || !token || loading) return loading;
	loading = new Promise<PostHog | null>((resolve) => {
		const start = () =>
			import('posthog-js')
				.then(({ default: ph }) => {
					ph.init(token, config);
					instance = ph;
					(window as unknown as { posthog: PostHog }).posthog = ph;
					for (const fn of queue.splice(0)) fn(ph);
					resolve(ph);
				})
				.catch(() => resolve(null));
		const ric = (window as unknown as { requestIdleCallback?: typeof requestIdleCallback })
			.requestIdleCallback;
		const whenIdle = () => (ric ? ric(start, { timeout: 4000 }) : setTimeout(start, 1500));
		// Wait for images and fonts first, so analytics never competes with the page itself.
		if (document.readyState === 'complete') whenIdle();
		else addEventListener('load', whenIdle, { once: true });
	});
	return loading;
}

/** Same call shape as the posthog-js methods the site uses. */
export const posthog = {
	get __loaded() {
		return !!instance?.__loaded;
	},
	capture(event: string, props?: Record<string, unknown>) {
		run((ph) => ph.capture(event, props));
	},
	captureException(err: unknown, props?: Record<string, unknown>) {
		run((ph) => ph.captureException(err, props));
	},
	identify(id: string, props?: Record<string, unknown>) {
		run((ph) => ph.identify(id, props));
	},
	/** Synchronous getters return undefined until the SDK has loaded. */
	get_distinct_id(): string | undefined {
		return instance?.get_distinct_id();
	},
	get_session_id(): string | undefined {
		return instance?.get_session_id?.();
	}
};

export default posthog;
